import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Button, Spinner, Badge, Modal } from "react-bootstrap";
import usePatientClinicalMemory from "../../hooks/usePatientClinicalMemory";
import HorizontalTimeline from "./HorizontalTimeline";
import StaleBanner from "./StaleBanner";
import AppointmentStatusCounts from "./AppointmentStatusCounts";

/**
 * ClinicalHistoryFullPanel: body-only full Clinical History view.
 * No Modal wrapper. Used both inside the standalone PatientClinicalHistoryModal
 * AND embedded inline inside EditAppointments.jsx.
 */

// Timeline entries are notes, documents from Patient Files, or letters the
// practice produced. Entries built before files joined have no entryId.
const idOf = (t) => (t ? t.entryId || t.sessionId : null);

function ListBlock({ title, items, emptyMessage }) {
  return (
    <section className="mb-3">
      <h6 className="text-uppercase small fw-semibold mb-2 px-3 py-2 rounded table-primary">{title}</h6>
      {items && items.length > 0 ? (
        <ul className="ps-3 mb-0">
          {items.map((it, i) => (
            <li key={i} className="mb-1">{it}</li>
          ))}
        </ul>
      ) : (
        <div className="text-muted small fst-italic">{emptyMessage || "Nothing recorded yet."}</div>
      )}
    </section>
  );
}

function ICD10Block({ candidates }) {
  return (
    <section className="mb-3">
      <h6 className="text-uppercase small fw-semibold mb-2 px-3 py-2 rounded table-primary">
        Suggested ICD10 Candidates
      </h6>
      <div className="alert alert-primary py-2 px-3 small mb-2 d-flex align-items-center">
        <i className="fa-light fa-sparkles text-primary me-2" />
        ICD10 suggestions are for clinician review only. They are not automatically applied.
      </div>
      {candidates && candidates.length > 0 ? (
        <div className="d-flex flex-column gap-2">
          {candidates.map((c, i) => (
            <div key={i} className="border rounded p-2">
              <div className="d-flex align-items-start gap-2">
                <Badge bg="secondary" className="text-uppercase">{c.code}</Badge>
                <div className="flex-grow-1">
                  <div className="fw-semibold">{c.description}</div>
                  <div className="small text-muted mb-1">{c.reason}</div>
                  <Badge
                    bg={c.confidence === "high" ? "success" : c.confidence === "medium" ? "warning" : "light"}
                    text={c.confidence === "low" ? "dark" : undefined}
                  >
                    Confidence: {c.confidence}
                  </Badge>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-muted small fst-italic">No ICD10 candidates suggested.</div>
      )}
    </section>
  );
}

export default function ClinicalHistoryFullPanel({
  active = true,
  patientId,
  practiceId,
  patientName,
  onBack,
  onClose,
  showHeader = true,
  // Called with a session id when the user asks to open the note a timeline
  // entry summarises. Supplied by the patient file, where the Notes section
  // can take over; absent in the calendar's compact modal.
  onOpenNote,
}) {
  const { memory, loading, error, isStale, pollingGaveUp, progress, queueState, refresh, checkAgain } =
    usePatientClinicalMemory(active ? patientId : null, practiceId);

  const timeline = useMemo(() => memory?.timeline || [], [memory]);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  // The file entry open in the viewer modal (an image or a PDF from
  // Patient Files), or null.
  const [viewerEntry, setViewerEntry] = useState(null);
  // "loading" until the image or frame reports it loaded, "error" when it
  // could not (an expired link, a blocked frame), else "ready".
  const [viewerState, setViewerState] = useState("loading");
  // What the viewer should render for an entry. A note's images are images
  // whatever they are called (mobile photos are stored as "media-photo-1",
  // no extension), so the caller says so; otherwise the blob path decides.
  const viewerKindOf = (entry) => {
    if (!entry) return "other";
    if (entry.isImage) return "image";
    const path = String(entry.fileUrl || entry.fileName || "").split("?")[0];
    if (/\.(jpe?g|png|gif|webp)$/i.test(path)) return "image";
    if (/\.pdf$/i.test(path)) return "pdf";
    return "other";
  };
  const openViewer = (entry) => {
    // Only an image or a PDF loads in place; anything else shows the
    // download link at once, with no spinner to wait on.
    setViewerState(viewerKindOf(entry) === "other" ? "ready" : "loading");
    setViewerEntry(entry);
  };

  useEffect(() => {
    if (!timeline.length) {
      setSelectedSessionId(null);
      return;
    }
    const newest = [...timeline].sort((a, b) => {
      const ta = a.date ? new Date(a.date).getTime() : 0;
      const tb = b.date ? new Date(b.date).getTime() : 0;
      return tb - ta;
    })[0];
    setSelectedSessionId((cur) =>
      cur && timeline.find((t) => idOf(t) === cur) ? cur : idOf(newest),
    );
  }, [timeline]);

  const selectedEntry = timeline.find((t) => idOf(t) === selectedSessionId);
  const selectedKind = selectedEntry?.kind || "NOTE";
  const status = memory?.indexStatus;
  const isInitialBuild = status === "NOT_INDEXED" || status === "INDEXING";
  const fullHistory = memory?.fullHistory || {};

  return (
    <div>
      {showHeader && (
        <div className="d-flex align-items-center mb-3 pb-2 border-bottom">
          {/* Left: Back (or empty spacer to keep title centred) */}
          <div style={{ flex: "1 1 0", minWidth: 0 }}>
            {onBack && (
              <Button variant="outline-secondary" size="sm" onClick={onBack} title="Back">
                <i className="fa-solid fa-arrow-left me-1" /> Back
              </Button>
            )}
          </div>
          {/* Centre: title */}
          <h5 className="mb-0 text-center" style={{ flex: "0 1 auto" }}>
            <i className="fa-solid fa-clock-rotate-left me-2 text-primary" />
            Clinical History{patientName ? ":" : ""}
            {patientName ? (
              <span className="text-muted small ms-1 fw-normal">{patientName}</span>
            ) : null}
          </h5>
          {/* Right: spacer matching left so title stays centred */}
          <div style={{ flex: "1 1 0" }} />
        </div>
      )}

      {(isStale || status === "INDEXING") && (
        <StaleBanner
          status={status}
          gaveUp={pollingGaveUp}
          progress={progress}
          queueState={queueState}
          onRetry={checkAgain}
        />
      )}

      {error && (
        <div className="alert alert-danger py-2 px-3 small mb-3">
          <i className="fa-solid fa-triangle-exclamation me-2" />
          {error}
        </div>
      )}

      {memory?.lastError && status === "FAILED" && (
        <div className="alert alert-danger py-2 px-3 small mb-3">
          <i className="fa-solid fa-circle-xmark me-2" />
          Last build failed: {memory.lastError.message}. Press Refresh to try again.
        </div>
      )}

      {loading && !memory ? (
        <div className="text-center py-5">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading patient history…
        </div>
      ) : isInitialBuild && (!timeline.length) ? (
        <div className="text-center py-4">
          <p className="text-muted mb-3">
            {status === "INDEXING"
              ? "Building this patient's clinical history for the first time. This may take up to a minute."
              : "No clinical history has been built yet for this patient."}
          </p>
          {status !== "INDEXING" && (
            <Button variant="primary" onClick={refresh}>
              <i className="fa-solid fa-rotate me-2" /> Build History
            </Button>
          )}
        </div>
      ) : (
        <>
          <AppointmentStatusCounts patientId={patientId} practiceId={practiceId} variant="inline" />

          <h6 className="text-uppercase small fw-semibold mb-2 px-3 py-2 rounded table-primary">Session Timeline</h6>
          <HorizontalTimeline
            practiceId={practiceId}
            timeline={timeline}
            selectedSessionId={selectedSessionId}
            onSelect={(entry) => setSelectedSessionId(idOf(entry))}
          />

          {/* Files the build read and judged not clinical stay off the
              timeline, but are named so nobody wonders where an upload went. */}
          {memory?.filesNotUsed?.length > 0 && (
            <div className="small text-muted mt-1">
              <i className="fa-solid fa-circle-info me-2" />
              Read but not used, as not clinical:{" "}
              {memory.filesNotUsed.map((f) => `${f.label || "file"}${f.documentType ? ` (${f.documentType})` : ""}`).join(", ")}.
            </div>
          )}

          {selectedEntry && (
            <div className="border-start border-3 border-primary ps-3 my-3 bg-light rounded-end py-2">
              <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
                <div className="text-muted small">
                  <strong>
                    {selectedKind === "FILE" ? "Selected document:" : selectedKind === "LETTER" ? "Selected letter:" : "Selected session:"}
                  </strong>{" "}
                  {selectedEntry.date
                    ? new Date(selectedEntry.date).toLocaleDateString("en-GB", {
                        day: "2-digit", month: "short", year: "numeric",
                      })
                    : "Date unknown"}
                  {" · "}
                  {selectedKind === "NOTE" ? (selectedEntry.status || "Session") : (selectedEntry.title || selectedEntry.status)}
                  {selectedEntry.sourceType === "SYNCTO_NOTE" ? " · SyncTo" : ""}
                </div>
                {/* The summary is a digest of a real note. Point at it, so the
                    history and the notes stop being two unconnected sections. */}
                {selectedKind === "NOTE" && onOpenNote && selectedEntry.sessionId && (
                  <Button
                    variant="link"
                    size="sm"
                    className="text-primary text-decoration-none p-0 ms-auto"
                    onClick={() => onOpenNote(String(selectedEntry.sessionId))}
                  >
                    <i className="fa-light fa-clipboard-medical me-2" />
                    Open note
                  </Button>
                )}
                {/* A document's digest points at the file itself in Patient
                    Files; the link is re-signed by the server on every read. */}
                {selectedKind === "FILE" && selectedEntry.fileUrl && (
                  <Button
                    variant="link"
                    size="sm"
                    className="text-primary text-decoration-none p-0 ms-auto"
                    onClick={() => openViewer(selectedEntry)}
                  >
                    <i className="fa-light fa-file-lines me-2" />
                    Open file
                  </Button>
                )}
              </div>
              {selectedEntry.unreadable && (
                <div className="alert alert-warning py-2 px-3 small mb-2">
                  <i className="fa-solid fa-circle-info me-2" />
                  Not read into the history. Open the file to review it yourself.
                </div>
              )}
              {selectedEntry.uninterpreted && (
                <div className="alert alert-info py-2 px-3 small mb-2">
                  <i className="fa-solid fa-circle-info me-2" />
                  Images are not interpreted by the history. Open the image, or its report, to read it.
                </div>
              )}
              <div style={{ whiteSpace: "pre-wrap" }}>{selectedEntry.summary}</div>
              {selectedKind === "FILE" && selectedEntry.keyFindings?.length > 0 && (
                <ul className="ps-3 mb-0 mt-2 small">
                  {selectedEntry.keyFindings.map((f, i) => (
                    <li key={i}>{f}</li>
                  ))}
                </ul>
              )}
              {/* The images that belong to this note (media photos, template
                  photo fields), for review. Listed, never interpreted. */}
              {selectedKind === "NOTE" && selectedEntry.images?.length > 0 && (
                <div className="mt-2">
                  <div className="small text-muted mb-1">
                    Images in this note ({selectedEntry.images.length}). Click one to open it.
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    {selectedEntry.images.map((im, i) => (
                      <Button
                        key={im.fileUrl || i}
                        variant="link"
                        className="p-0 border rounded overflow-hidden"
                        style={{ width: 96, height: 96, background: "var(--acc-header-bg)" }}
                        onClick={() => openViewer({ fileName: im.fileName, fileUrl: im.fileUrl, title: im.label || `Image ${i + 1}`, isImage: true })}
                        aria-label={`Open ${im.label || `image ${i + 1}`}`}
                      >
                        <img
                          src={im.fileUrl}
                          alt={im.label || `Image ${i + 1}`}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Full-width divider between the session detail and the 3-column body */}
          <hr className="my-4" />

          <Row className="g-0">
            {/* Column 1: History Summary + Treatment Themes */}
            <Col md={4} className="pe-md-3 border-end">
              <section className="mb-3">
                <h6 className="text-uppercase small fw-semibold mb-2 px-3 py-2 rounded table-primary">History Summary</h6>
                <p style={{ whiteSpace: "pre-wrap" }}>
                  {fullHistory.historySummaryParagraph || "No summary available yet."}
                </p>
              </section>
              <ListBlock
                title="Treatment Themes"
                items={fullHistory.treatmentThemes}
                emptyMessage="No recurring themes identified yet."
              />
              {/* Findings drawn from documents in Patient Files: imaging,
                  specialist opinions, pathology, discharge summaries. */}
              <ListBlock
                title="External Findings"
                items={fullHistory.externalFindings}
                emptyMessage="No findings from uploaded documents yet."
              />
            </Col>

            {/* Column 2: Suggested Focus for Today + Suggested Questions */}
            <Col md={4} className="px-md-3 border-end">
              <section className="mb-3">
                <h6 className="text-uppercase small fw-semibold mb-2 px-3 py-2 rounded table-primary">
                  Suggested Focus for Today
                </h6>
                <div className="alert alert-primary py-2 px-3 small mb-2 d-flex align-items-center">
                  <i className="fa-light fa-sparkles text-primary me-2" />
                  AI-suggested. Practitioner must review.
                </div>
                {fullHistory.suggestedFocusForToday?.length ? (
                  <ul className="ps-3 mb-0">
                    {fullHistory.suggestedFocusForToday.map((it, i) => (
                      <li key={i} className="mb-1">{it}</li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-muted small fst-italic">No suggested focus yet.</div>
                )}
              </section>
              <ListBlock
                title="Suggested Questions"
                items={fullHistory.suggestedQuestions}
                emptyMessage="No suggested questions yet."
              />
            </Col>

            {/* Column 3: ICD10 Candidates + Missing Information */}
            <Col md={4} className="ps-md-3">
              <ICD10Block candidates={fullHistory.suggestedICD10Candidates} />
              <ListBlock
                title="Missing Information"
                items={fullHistory.missingInformation}
                emptyMessage="No documentation gaps flagged."
              />
            </Col>
          </Row>

          {/* The file viewer: an image inline, a PDF in a frame, anything
              else as a download link. Closes with the X or the Close button;
              the link is re-signed by the server on every read. */}
          <Modal show={!!viewerEntry} onHide={() => setViewerEntry(null)} centered size="xl" scrollable>
            <Modal.Header
              closeButton
              style={{ background: "#CFE2FF" }}
              className="rounded-0 text-primary d-flex align-items-center"
            >
              <Modal.Title as="h6" className="m-0">
                {viewerEntry?.title || viewerEntry?.status || "File"}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body className="text-center p-2 position-relative" style={{ minHeight: "60vh" }}>
              {viewerEntry && viewerState === "loading" && (
                <div className="position-absolute top-50 start-50 translate-middle">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-2 mb-0">Loading file…</p>
                </div>
              )}
              {viewerEntry && viewerState === "error" && (
                <p className="my-5">
                  The file could not be shown here.{" "}
                  <a href={viewerEntry.fileUrl} target="_blank" rel="noopener noreferrer">Download it</a> instead.
                </p>
              )}
              {viewerEntry && viewerState !== "error" && viewerKindOf(viewerEntry) === "image" ? (
                <img
                  src={viewerEntry.fileUrl}
                  alt={viewerEntry.title || "Uploaded image"}
                  onLoad={() => setViewerState("ready")}
                  onError={() => setViewerState("error")}
                  style={{ maxWidth: "100%", maxHeight: "78vh", objectFit: "contain", visibility: viewerState === "ready" ? "visible" : "hidden" }}
                />
              ) : viewerEntry && viewerState !== "error" && viewerKindOf(viewerEntry) === "pdf" ? (
                <iframe
                  title={viewerEntry.title || "Uploaded document"}
                  src={viewerEntry.fileUrl}
                  onLoad={() => setViewerState("ready")}
                  onError={() => setViewerState("error")}
                  style={{ width: "100%", height: "78vh", border: 0 }}
                />
              ) : viewerEntry && viewerState !== "error" ? (
                <p className="my-5">
                  This file type cannot be shown here.{" "}
                  <a href={viewerEntry.fileUrl} target="_blank" rel="noopener noreferrer">Download it</a> instead.
                </p>
              ) : null}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-secondary" onClick={() => setViewerEntry(null)}>
                Close
              </Button>
            </Modal.Footer>
          </Modal>

          {/* No model name here: which AI deployment produced the history is
              an internal detail and must not appear anywhere in the UI. */}
          {memory?.aiMeta?.generatedAt && (
            <div className="text-muted small mt-3 pt-2 border-top">
              Generated {new Date(memory.aiMeta.generatedAt).toLocaleString("en-GB")}
            </div>
          )}
        </>
      )}

      <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={refresh}
          disabled={loading || status === "INDEXING"}
        >
          <i className="fa-solid fa-rotate me-1" /> Refresh
        </Button>
        {onClose && (
          <Button variant="secondary" size="sm" onClick={onClose}>Close</Button>
        )}
      </div>
    </div>
  );
}
